import * as React from 'react';
import { CssBaseline, ThemeProvider } from '@material-ui/core';
import { ApolloProvider } from '@apollo/react-hooks';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import moment from 'moment';
import Validator from 'validatorjs';
import { Router } from 'react-router-dom';
import { createBrowserHistory } from 'history';
import 'moment/locale/es';
import { getBase, refreshAndSet, setBase } from '../../utils/auth';
import { getApolloClient } from '../../utils/getApolloClient';
import AppRouter from './AppRouter';
import { getServerURLBase } from '../../utils/getServerURLBase';
import { GlobalCss } from './GlobalCss';
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary';
import { BankContextProvider } from '../../dictionaries/context/BankContext';
import { LoaderDialog } from '../../components/LoaderDialog/LoaderDialog';
import { useBrandingContext } from '../../branding';
import { enhanceHistoryPersistQuery } from '../../utils/enhanceHistoryPersistQuery';
import { CountryContextProvider } from '../../dictionaries/context/CountryContext';

moment.locale('es');
(Validator as any).useLang('es');

// REACT_APP_ENV_OVERRIDE=TEST yarn start // to change URL for DEV, TEST, PROD
setBase(getServerURLBase());
const client = getApolloClient(`${getBase()}/graphql`);

const history = enhanceHistoryPersistQuery(
  createBrowserHistory(),
  [ 'token', 'id', 'outDebtId' ],
);
const App: React.FC = () => {
  const { theme } = useBrandingContext();
  React.useEffect(() => {
    // https://css-tricks.com/the-trick-to-viewport-units-on-mobile/
    window.addEventListener('resize', () => document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`));
    // failsafe redirect to https
    if (
      process.env.REACT_APP_FORCE_HTTPS === 'true'
      && window.location.protocol === 'http:'
      && !/localhost/.test(window.location.href)
      && !/ec2-/.test(window.location.href)
    ) window.location.href = window.location.href.replace('http:', 'https:');
  }, []);

  const [ refreshing, setRefreshing ] = React.useState(true);
  React.useEffect(() => {
    refreshAndSet()
      .then(() => setRefreshing(false))
      .catch(() => setRefreshing(false));
  }, []);

  return (
    <Router history={history}>
      <ThemeProvider theme={theme}>
        <ApolloProvider client={client}>
          <BankContextProvider>
            <CountryContextProvider>
              <ErrorBoundary>
                <MuiPickersUtilsProvider utils={MomentUtils}>
                  <>
                    <CssBaseline />
                    <GlobalCss />
                    {refreshing && <LoaderDialog open />}
                    {!refreshing && (
                      <React.Suspense fallback={<LoaderDialog open />}>
                        <AppRouter />
                      </React.Suspense>
                    )}
                  </>
                </MuiPickersUtilsProvider>
              </ErrorBoundary>
            </CountryContextProvider>
          </BankContextProvider>
        </ApolloProvider>
      </ThemeProvider>
    </Router>
  );
};

export default App;
